import axios from "axios";

const API = "/api";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fileUrl(fileName) {
  return fileName ? `/uploads/${fileName}` : "";
}

function replaceShortcodes(template, data) {
  return String(template ?? "").replace(/\{\{([^}]+)\}\}/g, (_, rawKey) => {
    const key = String(rawKey || "").trim();
    return data[key] ?? `{{${key}}}`;
  });
}

function qrCodeUrl(value) {
  const text = String(value || "").trim();
  return text ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(text)}` : "";
}

function resultVerificationUrl(data) {
  const origin = window.location?.origin || "";
  const params = new URLSearchParams();
  if (data.registration_no) params.set("reg", data.registration_no);
  if (data.dob) params.set("dob", data.dob);
  return `${origin}/result-verification${params.toString() ? `?${params.toString()}` : ""}`;
}

function formatDateOnly(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).split(/[T ]/)[0] || String(value);
  }

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatDateSlash(value) {
  if (!value) return "";

  const raw = String(value).split(/[T ]/)[0];
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const dmyMatch = /^(\d{2})[-/](\d{2})[-/](\d{4})$/.exec(raw);
  if (dmyMatch) {
    return `${dmyMatch[1]}/${dmyMatch[2]}/${dmyMatch[3]}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return raw;

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

const removedMarksheetFields = new Set([
  "roll_no",
  "semester_rows",
  "total_marks",
  "full_marks",
  "pass_marks",
  "grade_point",
  "footer_note",
  "project_obtained_marks",
  "project_full_marks",
  "project_pass_marks",
]);

function popupDocument(title, body, orientation = "portrait") {
  const popup = window.open("", "_blank", "width=1100,height=1000");
  if (!popup) return;

  popup.document.write(`
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          body { margin: 0; background: #f3f4f6; font-family: "Segoe UI", sans-serif; color: #0f172a; }
          .print-shell { padding: 18px; }
          .sheet { margin: 0 auto; background: #fff; box-shadow: 0 16px 40px rgba(15,23,42,.12); }
          .sheet.a4 { position: relative; overflow: hidden; }
          .sheet.a4.portrait { width: 794px; height: 1123px; }
          .sheet.a4.landscape { width: 1123px; height: 794px; }
          .sheet.card { width: 680px; min-height: 420px; position: relative; overflow: hidden; border-radius: 24px; }
          .background-image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; display: block; z-index: 0; background: #fff; }
          .logo-box { position: absolute; left: 48px; top: 34px; width: 86px; height: 86px; border-radius: 22px; overflow: hidden; border: 1px solid rgba(148,163,184,.18); background: #fff; z-index: 3; }
          .logo-box img, .signature-box img, .student-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
          .signature-box { position: absolute; right: 68px; bottom: 72px; width: 150px; height: 58px; border-radius: 12px; overflow: hidden; border: 1px solid rgba(148,163,184,.16); background: #fff; z-index: 3; }
          .student-photo { position: absolute; right: 54px; top: 148px; width: 136px; height: 164px; border-radius: 20px; overflow: hidden; border: 1px solid rgba(148,163,184,.18); background: #fff; z-index: 3; }
          .simple-hero { padding: 26px 30px; color: #fff; background: linear-gradient(135deg, #4e1b24 0%, #7c2d3a 52%, #c2410c 100%); }
          .simple-body { padding: 26px 30px; display: grid; gap: 18px; }
          .simple-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
          .simple-card { border: 1px solid rgba(148,163,184,.18); border-radius: 18px; padding: 14px; background: rgba(255,255,255,.92); }
          .field-layer { position: absolute; white-space: pre-wrap; color: #0f172a; z-index: 2; }
          @media print {
            @page { size: A4 ${orientation}; margin: 0; }
            body { background: #fff; }
            .print-shell { padding: 0; }
            .sheet { box-shadow: none; }
          }
        </style>
      </head>
      <body>${body}</body>
    </html>
  `);
  popup.document.close();

  const waitForImages = () => {
    const images = Array.from(popup.document.images || []);
    return Promise.all(
      images.map((image) => {
        if (image.complete && image.naturalWidth !== 0) return Promise.resolve();
        return new Promise((resolve) => {
          image.onload = resolve;
          image.onerror = resolve;
        });
      })
    );
  };

  const waitForFonts = popup.document.fonts?.ready || Promise.resolve();
  Promise.all([waitForImages(), waitForFonts])
    .then(() => {
      popup.requestAnimationFrame(() => {
        window.setTimeout(() => {
          popup.focus();
          popup.print();
        }, 350);
      });
    })
    .catch(() => {
      window.setTimeout(() => {
        popup.focus();
        popup.print();
      }, 700);
    });
}

export async function fetchAmciDesign(documentType) {
  try {
    const res = await axios.get(`${API}/amci-designs/${documentType}`);
    return res.data || null;
  } catch (error) {
    return null;
  }
}

export async function fetchGradePoints() {
  try {
    const res = await axios.get(`${API}/grade-points`);
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    return [];
  }
}

export function resolveGradePointByPercentage(percentage, gradeRows) {
  const value = Number(percentage || 0);
  const rows = Array.isArray(gradeRows) ? gradeRows : [];
  const match = rows.find((row) => value >= Number(row.percent_from || 0) && value <= Number(row.percent_to || 0));

  return {
    grade: match?.grade_name || `${value.toFixed(2)}%`,
    grade_point: match ? Number(match.grade_point || 0).toFixed(2) : "",
  };
}

function buildSimpleDocumentHtml(documentType, design, data) {
  const form = design?.form || {};
  const uploads = form.uploads || {};
  const isIdCard = documentType === "idcard";
  const backgroundPreview = uploads[Object.keys(uploads)[0]] || "";
  const uploadKeys = Object.keys(uploads);
  const backBackgroundPreview = isIdCard ? uploads[uploadKeys[1]] || "" : "";
  const logoPreview = isIdCard ? uploads[uploadKeys[2]] || "" : uploads[uploadKeys[1]] || "";
  const signaturePreview = isIdCard ? "" : uploads[uploadKeys[2]] || "";
  const title = replaceShortcodes(form.headerTitle || (documentType === "admit" ? "Student Admit Card" : "Student ID Card"), data);
  const subtitle = replaceShortcodes(form.subTitle || "National Institute of Digital Careers and Skills", data);
  const bodyText = replaceShortcodes(
    form.bodyText ||
      (documentType === "admit"
        ? "Student: {{student_name}}\nRegistration No: {{registration_no}}\nCourse: {{course_name}}\nSession: {{session_year}}\nStudy Centre: {{study_centre}}\nStatus: {{status}}"
        : "Student: {{student_name}}\nRegistration No: {{registration_no}}\nCourse: {{course_name}}\nValid Upto: {{valid_upto}}\nContact: {{student_mobile}}\nStudy Centre: {{study_centre}}"),
    data
  );
  const footerNote = replaceShortcodes(form.footerNote || "Active AMCI design", data);
  const nameText = replaceShortcodes(form.studentNameCode || "{{student_name}}", data);
  const backgroundStyle = backgroundPreview
    ? "background:#fff;"
    : "background: linear-gradient(135deg, #fff7ed 0%, #ffffff 44%, #eff6ff 100%);";

  return `
    <div class="print-shell">
      <div class="sheet card" style="${backgroundStyle}">
        ${backgroundPreview ? `<img class="background-image" src="${escapeHtml(backgroundPreview)}" alt="" />` : ""}
        <div class="simple-hero" style="position:relative;z-index:1;">
          <h1 style="margin:0 0 8px;">${escapeHtml(title)}</h1>
          <p style="margin:0;opacity:.82;">${escapeHtml(subtitle)}</p>
        </div>
        <div class="simple-body" style="position:relative;z-index:1;">
          <div style="display:flex;justify-content:space-between;gap:18px;align-items:flex-start;flex-wrap:wrap;">
            <div style="flex:1 1 340px;">
              <div style="font-size:28px;font-weight:800;color:#7c2d3a;margin-bottom:12px;">${escapeHtml(nameText)}</div>
              <div style="line-height:1.85;color:#334155;">${escapeHtml(bodyText).replace(/\n/g, "<br/>")}</div>
            </div>
            <div style="display:grid;gap:12px;">
              ${logoPreview ? `<div style="width:86px;height:86px;border-radius:20px;overflow:hidden;border:1px solid rgba(148,163,184,.18);background:#fff;"><img src="${logoPreview}" alt="Logo" /></div>` : ""}
              ${data.student_photo ? `<div style="width:136px;height:164px;border-radius:20px;overflow:hidden;border:1px solid rgba(148,163,184,.18);background:#fff;"><img src="${data.student_photo}" alt="Student" /></div>` : ""}
            </div>
          </div>
          <div class="simple-grid">
            <div class="simple-card"><strong>Registration No</strong><div>${escapeHtml(data.registration_no)}</div></div>
            <div class="simple-card"><strong>Course</strong><div>${escapeHtml(data.course_name)}</div></div>
            <div class="simple-card"><strong>Session</strong><div>${escapeHtml(data.session_year)}</div></div>
            <div class="simple-card"><strong>Study Centre</strong><div>${escapeHtml(data.study_centre)}</div></div>
          </div>
          <div style="display:flex;justify-content:space-between;gap:12px;align-items:end;">
            <div style="color:#64748b;font-size:13px;">${escapeHtml(footerNote)}</div>
            ${signaturePreview ? `<div style="width:130px;height:52px;border-radius:12px;overflow:hidden;border:1px solid rgba(148,163,184,.16);background:#fff;"><img src="${signaturePreview}" alt="Signature" /></div>` : ""}
          </div>
        </div>
      </div>
      ${
        isIdCard && backBackgroundPreview
          ? `<div class="sheet card" style="background:#fff;margin-top:18px;"><img class="background-image" src="${escapeHtml(backBackgroundPreview)}" alt="" /></div>`
          : ""
      }
    </div>
  `;
}

function buildStructuredDocumentHtml(documentType, design, data) {
  const form = design?.form || {};
  const previewFields = design?.preview_fields || {};
  const fieldPositions = design?.field_positions || {};
  const uploads = form.uploads || {};
  const backgroundPreview = uploads[Object.keys(uploads)[0]] || "";
  const logoPreview = uploads[Object.keys(uploads)[1]] || "";
  const signaturePreview = uploads[Object.keys(uploads)[2]] || "";
  const bodyStyle = form.certificateBodyStyle || {};
  const isCertificate = documentType === "certificate";
  const certificateBodyWidth = Number(bodyStyle.bodyWidth || 760);
  const hasStructuredBackgroundImage = Boolean(backgroundPreview);
  const backgroundStyle = hasStructuredBackgroundImage
    ? "background:#fff;"
    : "background: linear-gradient(135deg, #fffaf5 0%, #ffffff 45%, #f8fafc 100%);";

  const fieldHtml = Object.entries(previewFields)
    .map(([key, value]) => {
      if (isCertificate && key === "verification_qr") return "";
      if (documentType === "marksheet" && key === "title") return "";
      if (documentType === "marksheet" && removedMarksheetFields.has(key)) return "";

      const position = fieldPositions[key];
      if (!position) return "";

      const renderedValue = replaceShortcodes(value, data);
      const isTitle = key === "title";
      const isFooter = key === "footer_note";
      const isModuleDetails = key === "module_details";
      const isRows = key === "semester_rows" || isModuleDetails;
      const isPhoto = key === "student_photo";
      const isQrCode = key === "qr_code";
      const isBody = key === "body_text";
      const isMarksheetCentreCode = documentType === "marksheet" && key === "centre_code";

      if (isPhoto) {
        if (!data.student_photo) return "";
        return `
          <div
            class="field-layer"
            style="
              left:${Number(position.x || 0)}px;
              top:${Number(position.y || 0)}px;
              width:118px;
              height:138px;
              border:1px solid rgba(30,105,173,.32);
              background:#fff;
              overflow:hidden;
              z-index:3;
            "
          ><img src="${escapeHtml(data.student_photo)}" alt="Student Photo" style="width:100%;height:100%;object-fit:cover;display:block;" /></div>
        `;
      }

      if (isQrCode) {
        const qrSrc = qrCodeUrl(data.qr_code || data.registration_no || data.certificate_no);
        if (!qrSrc) return "";
        return `
          <div
            class="field-layer"
            style="
              left:${Number(position.x || 0)}px;
              top:${Number(position.y || 0)}px;
              width:118px;
              height:118px;
              background:#fff;
              overflow:hidden;
              z-index:3;
            "
          ><img src="${escapeHtml(qrSrc)}" alt="QR Code" style="width:100%;height:100%;object-fit:contain;display:block;" /></div>
        `;
      }

      return `
        <div
          class="field-layer"
          style="
            left:${Number(position.x || 0)}px;
            top:${Number(position.y || 0)}px;
            font-weight:${isTitle ? 800 : isRows ? 600 : 700};
            font-size:${isTitle ? 34 : isBody ? Number(bodyStyle.fontSize || 20) : isFooter ? 16 : isModuleDetails ? 13 : isMarksheetCentreCode ? 14 : 20}px;
            color:${position.color || (isFooter ? "#475569" : "#0f172a")};
            line-height:${isModuleDetails ? 1.45 : isRows || isBody ? 1.8 : 1.5};
            max-width:${isRows ? 610 : isBody ? (isCertificate ? certificateBodyWidth : 560) : isTitle ? (isCertificate ? 700 : 460) : (isCertificate ? 680 : 520)}px;
            text-align:${isTitle ? "center" : "left"};
            letter-spacing:${isTitle ? ".03em" : "normal"};
            font-family:${isBody ? bodyStyle.fontFamily || "Georgia, serif" : "inherit"};
            ${isBody ? "z-index:2;" : ""}
          "
        >${isBody ? renderedValue : escapeHtml(renderedValue).replace(/\n/g, "<br/>")}</div>
      `;
    })
    .join("");

  return `
    <div class="print-shell">
      <div class="sheet a4 ${isCertificate ? "landscape" : "portrait"}" style="${backgroundStyle}">
        ${hasStructuredBackgroundImage ? `<img class="background-image" src="${escapeHtml(backgroundPreview)}" alt="" />` : ""}
        ${logoPreview ? `<div class="logo-box"><img src="${logoPreview}" alt="Logo" /></div>` : ""}
        ${data.student_photo && !isCertificate && !previewFields.student_photo ? `<div class="student-photo"><img src="${data.student_photo}" alt="Student" /></div>` : ""}
        ${fieldHtml}
        ${signaturePreview ? `<div class="signature-box"><img src="${signaturePreview}" alt="Signature" /></div>` : ""}
      </div>
    </div>
  `;
}

export function printAmciDocument(documentType, design, rawData) {
  const data = {
    student_name: "",
    father_name: "",
    registration_no: "",
    course_name: "",
    admission_date: "",
    course_completion_date: "",
    period_of_course: "",
    exam_date: "",
    exam_time: "",
    study_centre: "",
    centre_name: "",
    centre_address: "",
    session_year: "",
    grade: "",
    grade_point: "",
    certificate_no: "",
    issue_date: "",
    verification_qr: "Verification QR",
    qr_code: "",
    valid_upto: "",
    student_mobile: "",
    centre_code: "",
    barcode_value: "",
    total_obtained: "",
    total_full_marks: "",
    total_pass_marks: "",
    obtained_marks: "",
    theory_obtained_marks: "",
    theory_full_marks: "",
    theory_pass_marks: "",
    practical_obtained_marks: "",
    practical_full_marks: "",
    practical_pass_marks: "",
    project_assignment_obtained_marks: "",
    project_assignment_full_marks: "",
    project_assignment_pass_marks: "",
    viva_obtained_marks: "",
    viva_full_marks: "",
    viva_pass_marks: "",
    percentage: "",
    module_details: "",
    status: "",
    ...rawData,
  };
  data.issue_date = formatDateOnly(data.issue_date);
  data.admission_date = formatDateSlash(data.admission_date);
  data.course_completion_date = formatDateSlash(data.course_completion_date);
  if (!data.period_of_course && data.admission_date && data.course_completion_date) {
    data.period_of_course = `${data.admission_date} to ${data.course_completion_date}`;
  }
  if (!data.centre_name) {
    data.centre_name = data.study_centre;
  }
  if (!data.qr_code) {
    data.qr_code = resultVerificationUrl(data);
  }

  const body =
    documentType === "marksheet" || documentType === "certificate"
      ? buildStructuredDocumentHtml(documentType, design, data)
      : buildSimpleDocumentHtml(documentType, design, data);

  popupDocument(documentType.toUpperCase(), body, documentType === "certificate" ? "landscape" : "portrait");
}

export function buildMarksheetRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => `${row.module_title}: ${row.marks_obtained}/${row.max_marks} (${row.approval_status || "approved"})`)
    .join("\n");
}

export function buildModuleDetailsRows(rows) {
  const groups = (Array.isArray(rows) ? rows : []).reduce((accumulator, row) => {
    const semesterNo = Number(row?.semester_no || 1);
    const title = row?.semester_title || `Semester ${semesterNo}`;
    if (!accumulator[semesterNo]) {
      accumulator[semesterNo] = {
        semesterNo,
        title,
        modules: [],
      };
    }
    if (row?.module_title) {
      accumulator[semesterNo].modules.push(row.module_title);
    }
    return accumulator;
  }, {});

  return Object.values(groups)
    .sort((left, right) => left.semesterNo - right.semesterNo)
    .map((group) => `${group.title}: ${group.modules.join(", ")}`)
    .join("\n");
}

export function calculatePassMarks(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    const explicitPassMarks = row?.pass_marks ?? row?.pass_mark ?? row?.minimum_marks;
    const value = explicitPassMarks === undefined || explicitPassMarks === null || explicitPassMarks === ""
      ? Number(row?.max_marks || 0) * 0.3
      : Number(explicitPassMarks || 0);
    return sum + value;
  }, 0);
}

export function sumMarksField(rows, key) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + Number(row?.[key] || 0), 0);
}

function sumFirstAvailableMarksField(rows, keys) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => {
    const value = keys.map((key) => row?.[key]).find((item) => item !== undefined && item !== null && item !== "");
    return sum + Number(value || 0);
  }, 0);
}

export function buildMarksSummary(rows) {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const theoryObtained = sumMarksField(sourceRows, "marks_obtained");
  const theoryFull = sumMarksField(sourceRows, "max_marks");
  const theoryPass = sumMarksField(sourceRows, "pass_marks");
  const practicalObtained = sumMarksField(sourceRows, "practical_marks_obtained");
  const practicalFull = sumMarksField(sourceRows, "practical_max_marks");
  const practicalPass = sumMarksField(sourceRows, "practical_pass_marks");
  const projectObtained = sumFirstAvailableMarksField(sourceRows, ["project_marks_obtained", "project_assignment_obtained_marks"]);
  const projectFull = sumFirstAvailableMarksField(sourceRows, ["project_max_marks", "project_assignment_full_marks"]);
  const projectPass = sumFirstAvailableMarksField(sourceRows, ["project_pass_marks", "project_assignment_pass_marks"]);
  const vivaObtained = sumMarksField(sourceRows, "viva_marks_obtained");
  const vivaFull = sumMarksField(sourceRows, "viva_max_marks");
  const vivaPass = sumMarksField(sourceRows, "viva_pass_marks");
  const totalObtained = theoryObtained + practicalObtained + projectObtained + vivaObtained;
  const totalFull = theoryFull + practicalFull + projectFull + vivaFull;
  const totalPass = theoryPass + practicalPass + projectPass + vivaPass;
  const percentage = totalFull > 0 ? ((totalObtained / totalFull) * 100).toFixed(2) : "0.00";

  return {
    theoryObtained,
    theoryFull,
    theoryPass,
    practicalObtained,
    practicalFull,
    practicalPass,
    projectObtained,
    projectFull,
    projectPass,
    vivaObtained,
    vivaFull,
    vivaPass,
    totalObtained,
    totalFull,
    totalPass,
    percentage,
  };
}

export function resolveStudentPhoto(row) {
  if (row?.photo?.startsWith?.("data:") || row?.photo?.startsWith?.("http")) {
    return row.photo;
  }

  return fileUrl(row?.photo);
}
